import React, { useState } from 'react';
import axios from 'axios';
import { 
  Container, 
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  Button,
  createTheme,
  ThemeProvider,
  Divider,
  Stack
} from '@mui/material';
import { 
  Dashboard as DashboardIcon, 
  Article as ArticleIcon, 
  AccountBalance as GovernmentIcon,
  People as UserIcon,
  Settings as SettingsIcon,
  ListAlt,
  VpnKey as KeyIcon,
  TrendingUp as StatsIcon
} from '@mui/icons-material';
import { blue, green, orange, purple, red, teal } from '@mui/material/colors';
import AdminNavbar from './AdminNavbar';
import FunctionalKeysPage, { PREDEFINED_FUNCTIONAL_KEYS } from './FunctionalKeysPage';
import GovernmentInterestsListPage from './GovernmentInterestsListPage';
import CreateGovernmentInterestPage from './CreateGovernmentInterestPage';
import baseURL from '../../baseURL';
import { useLocation, useNavigate, Routes, Route } from 'react-router-dom';

const theme = createTheme({
  palette: {
    primary: {
      main: blue[600],
    },
    secondary: {
      main: teal[500],
    },
    background: {
      default: '#f4f6f8',
      paper: '#ffffff'
    }
  },
  typography: {
    fontFamily: 'Roboto, Arial, sans-serif',
    h4: {
      fontWeight: 600,
      color: blue[700]
    }
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          transition: 'transform 0.3s ease-in-out',
          '&:hover': {
            transform: 'scale(1.02)'
          }
        }
      }
    }
  }
});

// Tab Components with Enhanced Styling
const TabCard = ({ title, icon, color, children }) => (
  <Card 
    elevation={3} 
    sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      borderLeft: `5px solid ${color}`
    }}
  >
    <CardHeader
      avatar={
        <Avatar sx={{ bgcolor: color }}>
          {icon}
        </Avatar>
      }
      title={
        <Typography variant="h6" sx={{ color: blue[700], fontWeight: 600 }}>
          {title}
        </Typography>
      }
    />
    <CardContent sx={{ flexGrow: 1 }}>
      {children}
    </CardContent>
  </Card>
);

const AdminDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Determine current tab based on URL
  const getCurrentTab = () => {
    const path = location.pathname;
    if (path === '/admin-dashboard') return 0;
    if (path === '/admin/government-interests') return 1;
    if (path === '/admin/functional-keys') return 2;
    if (path === '/admin/article-management') return 3;
    if (path === '/admin/user-management') return 4;
    if (path === '/admin/settings') return 5;
    return 0;
  };

  const currentTab = getCurrentTab();

  // Handle tab change from navbar
  const handleTabChange = (tabIndex) => {
    navigate(getTabPath(tabIndex));
  };

  // Get path for a given tab index
  const getTabPath = (index) => {
    switch(index) {
      case 0: return '/admin-dashboard';
      case 1: return '/admin/government-interests';
      case 2: return '/admin/functional-keys';
      case 3: return '/admin/article-management';
      case 4: return '/admin/user-management';
      case 5: return '/admin/settings';
      default: return '/admin-dashboard';
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
        <AdminNavbar onTabChange={handleTabChange} />
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
          <Routes>
            <Route path="/admin-dashboard" element={
              <Grid container spacing={3}>
                {/* Stats Cards */}
                <Grid item xs={12} md={3}>
                  <Card sx={{ 
                    bgcolor: blue[50],
                    height: '100%',
                    transition: 'transform 0.2s',
                    '&:hover': { transform: 'translateY(-4px)' }
                  }}>
                    <CardContent>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Avatar sx={{ bgcolor: blue[500] }}>
                          <StatsIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="h4" color="primary">150</Typography>
                          <Typography variant="subtitle2" color="text.secondary">Total Articles</Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                  <Card sx={{ 
                    bgcolor: green[50],
                    height: '100%',
                    transition: 'transform 0.2s',
                    '&:hover': { transform: 'translateY(-4px)' }
                  }}>
                    <CardContent>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Avatar sx={{ bgcolor: green[500] }}>
                          <GovernmentIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="h4" color="success.main">45</Typography>
                          <Typography variant="subtitle2" color="text.secondary">Government Interests</Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                  <Card sx={{ 
                    bgcolor: orange[50],
                    height: '100%',
                    transition: 'transform 0.2s',
                    '&:hover': { transform: 'translateY(-4px)' }
                  }}>
                    <CardContent>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Avatar sx={{ bgcolor: orange[500] }}>
                          <KeyIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="h4" color="warning.main">12</Typography>
                          <Typography variant="subtitle2" color="text.secondary">Active Keys</Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                  <Card sx={{ 
                    bgcolor: purple[50],
                    height: '100%',
                    transition: 'transform 0.2s',
                    '&:hover': { transform: 'translateY(-4px)' }
                  }}>
                    <CardContent>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Avatar sx={{ bgcolor: purple[500] }}>
                          <UserIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="h4" color="secondary.main">89</Typography>
                          <Typography variant="subtitle2" color="text.secondary">Total Users</Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Quick Actions */}
                <Grid item xs={12}>
                  <Card sx={{ mt: 3 }}>
                    <CardHeader 
                      title="Quick Actions" 
                      titleTypography={{ variant: 'h6', color: 'primary.main' }}
                    />
                    <Divider />
                    <CardContent>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                          <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<ArticleIcon />}
                            fullWidth
                            sx={{ height: '48px' }}
                          >
                            New Article
                          </Button>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Button
                            variant="outlined"
                            color="secondary"
                            startIcon={<GovernmentIcon />}
                            fullWidth
                            sx={{ height: '48px' }}
                          >
                            Add Interest
                          </Button>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Button
                            variant="outlined"
                            color="warning"
                            startIcon={<KeyIcon />}
                            fullWidth
                            sx={{ height: '48px' }}
                          >
                            Generate Key
                          </Button>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Button
                            variant="outlined"
                            color="info"
                            startIcon={<ListAlt />}
                            fullWidth
                            sx={{ height: '48px' }}
                          >
                            View Logs
                          </Button>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            } />
            
            <Route path="/admin/government-interests" element={
              <Container maxWidth="xl" sx={{ mt: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Card elevation={3} sx={{ height: '100%' }}>
                      <CardHeader 
                        title="Create Government Interest" 
                        avatar={<Avatar sx={{ bgcolor: red[500] }}><GovernmentIcon /></Avatar>} 
                      />
                      <CardContent>
                        <CreateGovernmentInterestPage />
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Card elevation={3}>
                      <CardHeader 
                        title="Government Interests List" 
                        avatar={<Avatar sx={{ bgcolor: green[500] }}><ListAlt /></Avatar>} 
                      />
                      <CardContent>
                        <GovernmentInterestsListPage />
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Container>
            } />
            
            <Route path="/admin/functional-keys" element={
              <Container maxWidth="xl" sx={{ mt: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Card elevation={3} sx={{ height: '100%' }}>
                      <CardHeader 
                        title="Create Functional Key" 
                        avatar={<Avatar sx={{ bgcolor: blue[500] }}><KeyIcon /></Avatar>} 
                      />
                      <CardContent>
                        <Typography variant="body2" color="text.secondary">
                          Create and initialize new functional keys with specific percentages.
                        </Typography>
                        <Button 
                          variant="contained" 
                          color="primary" 
                          fullWidth 
                          sx={{ mt: 2 }}
                          onClick={() => {
                            // Implement initialization logic from FunctionalKeysPage
                            const initializeFunctionalKeys = async () => {
                              try {
                                const token = localStorage.getItem('token');
                                
                                // Create each predefined key
                                for (let key of PREDEFINED_FUNCTIONAL_KEYS) {
                                  await axios.post(`${baseURL}/functional-keys/create`, 
                                    { 
                                      keyName: key.keyName, 
                                      keyNumber: key.keyNumber, 
                                      percentage: key.defaultPercentage 
                                    },
                                    {
                                      headers: { 
                                        'Authorization': `Bearer ${token}`,
                                        'Content-Type': 'application/json'
                                      }
                                    }
                                  );
                                }
                              } catch (err) {
                                console.error('Failed to initialize functional keys', err);
                              }
                            };

                            initializeFunctionalKeys();
                          }}
                        >
                          Initialize Functional Keys
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Card elevation={3}>
                      <CardHeader 
                        title="Functional Keys List" 
                        avatar={<Avatar sx={{ bgcolor: teal[500] }}><ListAlt /></Avatar>} 
                      />
                      <CardContent>
                        <FunctionalKeysPage />
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Container>
            } />
            
            {/* Add other routes for article management, user management, and settings */}
          </Routes>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default AdminDashboard;